import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/utils/axios";

import cls from "./ClubRulesCreate.module.scss";

const STATUSES = ["active", "inactive"];

const ClubRulesCreate = () => {
  const { cityId, streetId, clubId } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    status: "active",
    title: "",
    description: "",
    footer: "",
  });

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");

  const backToRulesPath = useMemo(() => {
    if (!cityId || !streetId || !clubId) return "/admin/cities";
    return `/admin/cities/single/${cityId}/streets/${streetId}/clubs/${clubId}/rules`;
  }, [cityId, streetId, clubId]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onCreate = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess("");

    if (!form.title.trim()) {
      setErr("Title is required.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        status: form.status,
        title: form.title.trim(),
        description: form.description,
        footer: form.footer,
      };

      const { data } = await api.post(
        `/v1/admin/cities/${cityId}/streets/${streetId}/clubs/${clubId}/rules`,
        payload
      );

      const createdId = data?._id;
      if (!createdId) {
        // fallback: go back to list if API doesn't return _id
        navigate(backToRulesPath);
        return;
      }

      // ✅ redirect to single rule page
      navigate(
        `/admin/cities/single/${cityId}/streets/${streetId}/clubs/${clubId}/rules/${createdId}`
      );
    } catch (e) {
      const msg =
        e?.response?.data?.message || e?.message || "Failed to create rule.";
      setErr(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={cls.page}>
      <header className={cls.header}>
        <div className={cls.leftHead}>
          <Link className={cls.backLink} to={backToRulesPath}>
            ← Back to Rules
          </Link>

          <p className={cls.eyebrow}>Admin · Club Rules</p>
          <h1 className={cls.title}>Create Rule</h1>
          <p className={cls.subtitle}>
            Create a new rule, then upload images on the rule page.
          </p>
        </div>
      </header>

      {err && <div className={cls.error}>⚠ {err}</div>}
      {success && <div className={cls.success}>{success}</div>}

      <section className={cls.card}>
        <div className={cls.cardTop}>
          <h2 className={cls.cardTitle}>Rule Details</h2>
          <p className={cls.cardHint}>
            Status, title, description and footer only.
          </p>
        </div>

        <form className={cls.form} onSubmit={onCreate}>
          <div className={cls.formRow}>
            <div className={cls.field}>
              <label className={cls.label}>Status</label>
              <select
                className={cls.select}
                value={form.status}
                onChange={(e) => setField("status", e.target.value)}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <p className={cls.help}>
                Active shows to members. Inactive is draft.
              </p>
            </div>

            <div className={cls.field}>
              <label className={cls.label}>Title *</label>
              <input
                className={cls.input}
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
                placeholder="Respect & Consent"
                required
              />
            </div>
          </div>

          <div className={cls.field}>
            <label className={cls.label}>Description</label>
            <textarea
              className={cls.input}
              rows={5}
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              placeholder="Write the full rule here…"
            />
          </div>

          <div className={cls.field}>
            <label className={cls.label}>Footer</label>
            <textarea
              className={cls.input}
              rows={3}
              value={form.footer}
              onChange={(e) => setField("footer", e.target.value)}
              placeholder="Short footer note, disclaimer, or reminder…"
            />
          </div>

          <div className={cls.actions}>
            <button
              className={cls.secondaryBtn}
              type="button"
              disabled={saving}
              onClick={() => navigate(backToRulesPath)}
            >
              Cancel
            </button>

            <button className={cls.primaryBtn} type="submit" disabled={saving}>
              {saving ? "Creating…" : "Create Rule"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default ClubRulesCreate;
